import { useEffect, useRef, useState } from "react";
import { radarLabApi } from "services/radarLabApi";
import { isActiveRun } from "utils/radarLab";

export const STATUS_POLL_MS = 10000;
export const RUN_POLL_MS = 5000;
export const DATA_POLL_MS = 10000;
export const VALIDATION_POLL_MS = 120000;
const READINGS_PAGE = 3000;
const READINGS_PAGES_MAX = 10;

// A counter that goes up every `ms` while the tab is visible (and `on`).
function usePoll(ms, on = true) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") setTick((n) => n + 1);
    }, ms);
    return () => clearInterval(timer);
  }, [ms, on]);
  return tick;
}

// The clock for countdowns, once a second.
export function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(timer);
  }, [ms]);
  return now;
}

// GET lab/status/ for the chosen node and controller, every 10 s.
export function useLabStatus(selection, refresh = 0) {
  const [state, setState] = useState({ data: null, error: "", loading: true });
  const tick = usePoll(STATUS_POLL_MS);
  const node = selection?.node || "";
  const controller = selection?.controller || "";
  useEffect(() => {
    const abort = new AbortController();
    radarLabApi
      .status({ node, controller }, abort.signal)
      .then((data) => {
        if (!abort.signal.aborted) setState({ data, error: "", loading: false });
      })
      .catch((error) => {
        if (!abort.signal.aborted)
          setState((old) => ({
            data: old.data,
            error: error.message,
            loading: false,
          }));
      });
    return () => abort.abort();
  }, [node, controller, refresh, tick]);
  return state;
}

// The lab run history: on load and whenever `refresh` changes.
export function useLabRuns(refresh = 0) {
  const [state, setState] = useState({ runs: [], count: 0, error: "", loading: true });
  useEffect(() => {
    const abort = new AbortController();
    radarLabApi
      .runs({ limit: 20 }, abort.signal)
      .then((data) => {
        if (!abort.signal.aborted)
          setState({
            runs: data?.runs || [],
            count: data?.count ?? (data?.runs || []).length,
            error: "",
            loading: false,
          });
      })
      .catch((error) => {
        if (!abort.signal.aborted)
          setState((old) => ({ ...old, error: error.message, loading: false }));
      });
    return () => abort.abort();
  }, [refresh]);
  return state;
}

// One lab run's detail, every 5 s while it is active.
export function useLabRun(id, refresh = 0) {
  const [state, setState] = useState({ id: null, data: null, error: "" });
  const current = state.id === id ? state.data : null;
  const tick = usePoll(RUN_POLL_MS, !!id && isActiveRun(current));
  useEffect(() => {
    if (!id) return undefined;
    const abort = new AbortController();
    radarLabApi
      .run(id, abort.signal)
      .then((data) => {
        if (!abort.signal.aborted) setState({ id, data, error: "" });
      })
      .catch((error) => {
        if (!abort.signal.aborted)
          setState((old) => ({
            id,
            data: old.id === id ? old.data : null,
            error: error.message,
          }));
      });
    return () => abort.abort();
  }, [id, refresh, tick]);
  return state.id === id ? state : { id, data: null, error: "" };
}

const timeOf = (point) => {
  const t = Date.parse(point?.t);
  return Number.isFinite(t) ? t : null;
};

// Node 1's readings in a lab run (with the level each maps to): all of them
// once, then only newer ones every 10 s while the run is active.
export function useLabReadings(id, active = false, refresh = 0) {
  const [state, setState] = useState({ id: null, points: [], error: "" });
  const latest = useRef(state);
  latest.current = state;
  const tick = usePoll(DATA_POLL_MS, !!id && active);
  const [more, setMore] = useState(0);
  useEffect(() => {
    if (!id) return undefined;
    const abort = new AbortController();
    const known = latest.current.id === id ? latest.current.points : [];
    const since = known.length ? known[known.length - 1].t : undefined;
    radarLabApi
      .readings(id, { since }, abort.signal)
      .then((data) => {
        if (abort.signal.aborted) return;
        const fresh = data?.points || [];
        setState((old) => {
          const base = old.id === id ? old.points : [];
          const after = base.length ? timeOf(base[base.length - 1]) : null;
          const added =
            after === null
              ? fresh
              : fresh.filter((p) => (timeOf(p) ?? -Infinity) > after);
          return { id, points: [...base, ...added], error: "" };
        });
        if (fresh.length >= READINGS_PAGE && more < READINGS_PAGES_MAX)
          setMore((n) => n + 1);
      })
      .catch((error) => {
        if (!abort.signal.aborted)
          setState((old) => ({
            id,
            points: old.id === id ? old.points : [],
            error: error.message,
          }));
      });
    return () => abort.abort();
    // `more` asks for the next page of a long run at once.
  }, [id, tick, refresh, more]);
  return state.id === id ? state : { id, points: [], error: "" };
}

// The lab run's newest radar captures (at most 100), every 10 s while active.
export function useLabCaptures(id, active = false, refresh = 0) {
  const [state, setState] = useState({ id: null, captures: [], unit: null, error: "" });
  const tick = usePoll(DATA_POLL_MS, !!id && active);
  useEffect(() => {
    if (!id) return undefined;
    const abort = new AbortController();
    radarLabApi
      .captures(id, { limit: 100, mode: "all" }, abort.signal)
      .then((data) => {
        if (!abort.signal.aborted)
          setState({
            id,
            captures: data?.captures || [],
            unit: data?.db_unit || null,
            error: "",
          });
      })
      .catch((error) => {
        if (!abort.signal.aborted)
          setState((old) => ({
            id,
            captures: old.id === id ? old.captures : [],
            unit: old.id === id ? old.unit : null,
            error: error.message,
          }));
      });
    return () => abort.abort();
  }, [id, tick, refresh]);
  return state.id === id ? state : { id, captures: [], unit: null, error: "" };
}

// GET lab/validation/: the ruler camera against the node over the last `days`,
// for the chosen node and controller; at once for a new node, controller,
// window or `refresh`, and again every 2 min (the reader's pace), or only on
// every `every`-th of those ticks for a caller whose request is heavy.
export function useLabValidation({ node, controller, days }, refresh = 0, { every = 1 } = {}) {
  const key = `${node || ""}|${controller || ""}|${days}`;
  const [state, setState] = useState({ key: null, data: null, error: "", loading: false });
  const ticks = usePoll(VALIDATION_POLL_MS, !!node);
  const tick = Math.floor(ticks / Math.max(1, every));
  useEffect(() => {
    if (!node) return undefined;
    const abort = new AbortController();
    setState((old) => (old.key === key ? { ...old, loading: true } : { key, data: null, error: "", loading: true }));
    radarLabApi
      .validation({ node, controller, days }, abort.signal)
      .then((data) => {
        if (!abort.signal.aborted) setState({ key, data, error: "", loading: false });
      })
      .catch((error) => {
        if (!abort.signal.aborted)
          setState((old) => ({
            key,
            data: old.key === key ? old.data : null,
            error: error.message,
            loading: false,
          }));
      });
    return () => abort.abort();
  }, [key, node, controller, days, refresh, tick]);
  return state.key === key ? state : { key, data: null, error: "", loading: !!node };
}
